import { useEffect, useState } from "react";
import {
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  Avatar,
} from "@mui/material";
import Tile from "components/Tile.jsx";
import { backendGet, backendPost } from "services/dataService.js";
import { useSnackbar } from "notistack";

export default function InternalClaimReportSettingsPanel({ customerId }) {
  const { enqueueSnackbar } = useSnackbar();
  const [updating, setUpdating] = useState({});
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const aussendienstler = users.filter(
    (user) =>
      user.role === "kd_aussendienst" || user.role === "kd_eingeschraenkt"
  );

  useEffect(() => {
    const fetchUsers = async () => {
      await backendGet(`data/kundes/${customerId}/users`, {
        label: "Empfänger interner Schadenreport",
        enqueueSnackbar,
        setIsLoading,
        setResult: (res) => {
          setUsers(res.data || []);
        },
      });
    };
    fetchUsers();
  }, [customerId]);

  const handleToggleRecipient = async (user_id, current_status) => {
    try {
      await backendPost(
        `data/kundes/${customerId}/users/${user_id}/internal-recipient`,
        {
          toSend: {
            user_id: user_id,
            enabled: !current_status,
          },
          setIsLoading,
          setResult: (res) => {
            // update users state with new uodated user data
            setUsers((prevUsers) => {
              return prevUsers.map((user) => {
                if (user.id === user_id) {
                  return {
                    ...res.data,
                  };
                }
                return user;
              });
            });
          },
          label: "Empfänger interner Schadenreport",
          enqueueSnackbar,
        }
      );
    } catch (error) {
      console.error("Failed to toggle recipient", error);
    }
  };

  const handleToggleAussendienstRole = async (
    user_id,
    is_currently_restricted
  ) => {
    try {
      await backendPost(
        `data/kundes/${customerId}/users/${user_id}/staff-restricted`,
        {
          toSend: {
            user_id: user_id,
            restricted: !is_currently_restricted,
          },
          setIsLoading,
          setResult: (res) => {
            // update users state with new uodated user data
            setUsers((prevUsers) => {
              return prevUsers.map((user) => {
                if (user.id === user_id) {
                  return {
                    ...res.data,
                  };
                }
                return user;
              });
            });
          },
          label: "Außendienst-Berechtigung",
          enqueueSnackbar,
        }
      );
    } catch (error) {
      console.error("Failed to toggle aussendienst role", error);
    }
  };

  const getUserDisplayName = (user) => {
    if (user.full_name) return user.full_name;
    if (user.vorname && user.nachname) {
      return `${user.vorname} ${user.nachname}`.trim();
    }
    return user.email;
  };

  const getUserInitials = (user) => {
    if (user.vorname && user.nachname) {
      return `${user.vorname[0]}${user.nachname[0]}`.toUpperCase();
    }
    if (user.email) {
      return user.email.substring(0, 2).toUpperCase();
    }
    return "??";
  };

  const receivesInternalReport = (user) => {
    return user.permissions.includes(
      "claim.customer.receive_pre_check_customer"
    );
  };

  const isRestrictedAussendienst = (user) => {
    return user.role === "kd_eingeschraenkt";
  };

  return (
    <>
      <Tile sx={{ mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          Außendienstmitarbeiter
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Wählen Sie aus, welche Außendienstmitarbeiter Schäden nur kundenintern
          einreichen dürfen.
        </Typography>

        {aussendienstler && aussendienstler.length > 0 ? (
          <List>
            {aussendienstler.map((user) => (
              <ListItem key={user.id} divider>
                <Avatar sx={{ mr: 2 }}>{getUserInitials(user)}</Avatar>
                <ListItemText
                  primary={getUserDisplayName(user)}
                  secondary={user.email}
                />
                <ListItemSecondaryAction>
                  <Switch
                    edge="end"
                    checked={isRestrictedAussendienst(user)}
                    onChange={() =>
                      handleToggleAussendienstRole(
                        user.id,
                        isRestrictedAussendienst(user)
                      )
                    }
                    disabled={updating[user.id]}
                  />
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        ) : (
          <Box sx={{ p: 2, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              Keine Verwaltungsbenutzer für diesen Kunden gefunden.
            </Typography>
          </Box>
        )}
      </Tile>
      <Tile sx={{ mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          Empfänger interner Schadenreport
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Wählen Sie aus, welche Verwaltungsbenutzer interne Schadenreports für
          diesen Kunden erhalten sollen.
        </Typography>

        {users && users.length > 0 ? (
          <List>
            {users.map((user) => (
              <ListItem key={user.id} divider>
                <Avatar sx={{ mr: 2 }}>{getUserInitials(user)}</Avatar>
                <ListItemText
                  primary={getUserDisplayName(user)}
                  secondary={user.email}
                />
                <ListItemSecondaryAction>
                  <Switch
                    edge="end"
                    checked={receivesInternalReport(user)}
                    onChange={() =>
                      handleToggleRecipient(
                        user.id,
                        receivesInternalReport(user)
                      )
                    }
                    disabled={updating[user.id]}
                  />
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        ) : (
          <Box sx={{ p: 2, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              Keine Verwaltungsbenutzer für diesen Kunden gefunden.
            </Typography>
          </Box>
        )}
      </Tile>
    </>
  );
}
